import { Alert, DrilldownCell, SimpleTable, SeverityPill, TableSkeleton } from '@/components/ui'
import { useSkeletonOnConfigChange } from '@/hooks'
import useCompany from '@/hooks/useCompany'
import { validateDateRangeWithin13Months } from '@/insights/common/formatUtils'
import { buildQuery } from '@/insights/common/queryUtils'
import Caption, { addDateToCaptionFields } from '@/insights/components/Caption'
import { DateOptions } from '@/insights/components/CustomSingleSelect'
import { getSeverityLabel } from '@/insights/components/SeverityDropdown'
import {
  ApiService,
  type InsightConfigurationType,
  type InsightDefinition,
  InsightParametersType,
  InsightStatusCode,
  isInsightStatus,
} from '@/types/dashboard-types'
import { downloadCsv } from '@/utils/csvExport'

export interface AlertAcknowledgementSummaryParams extends InsightParametersType {
  companyId?: number
  alertConfigIds?: string
  severity?: string[]
  acknowledged?: string
  date?: string
  from?: string
  to?: string
}

interface ApiRow {
  alertConfigId: string
  alertName: string
  severity: number
  acknowledged: number
  notAcknowledged: number
}

const AlertAcknowledgementSummary: InsightDefinition<AlertAcknowledgementSummaryParams, ApiRow[]> = {
  active: true,
  slug: 'alert/acknowledgement-summary',
  category: 'alert',
  name: 'Alert Acknowledgement Summary',
  description:
    'Summary of acknowledged and unacknowledged alert counts per alert name for companies with acknowledgement enabled',
  defaultWidth: 'medium',
  defaultAspectRatio: 16 / 9,

  configuration: {
    transform: ({ parameters, field }) => {
      if (field === 'severity') return { ...parameters, alertConfigIds: undefined }
      if (field === 'alertConfigIds') return { ...parameters, severity: undefined }
      if (field === 'date' && parameters.date !== 'custom') {
        return { ...parameters, from: undefined, to: undefined }
      }
      return parameters
    },

    controls: [
      { id: 'companyId', control: 'CompanyDropdown', required: true, acknowledgementOnly: true },
      { id: 'note', control: 'Label', label: 'Choose either Severity or Alert Name' },
      {
        id: 'alertConfigIds',
        control: 'AlertNameDropdown',
        required: false,
        acknowledgementOnly: true,
        disableUserSubscriptionFilter: true,
      },
      { id: 'severity', control: 'SeverityDropdown' },
      { id: 'date', control: 'CustomSingleSelect', options: DateOptions },
      {
        id: 'customDate',
        label: 'Raised Date',
        control: 'DateTimeRange',
        dependsOn: 'date',
        dependsOnValue: 'custom',
      },
    ],

    isValid: (configuration: AlertAcknowledgementSummaryParams) => {
      if (!configuration.companyId) {
        return 'Company is required.'
      }

      const hasSeverity = configuration.severity && configuration.severity.length > 0
      const hasAlert = configuration.alertConfigIds && configuration.alertConfigIds.length > 0

      if (!hasSeverity && !hasAlert) {
        return 'Choose either Alert Name or Severity'
      }
      if (hasSeverity && hasAlert) {
        return 'Choose either Alert Name or Severity, not both'
      }

      if (configuration.date === 'custom') {
        if (!configuration.from && !configuration.to) {
          return 'Enter the custom date for Raised Date'
        }
        if (configuration.from && configuration.to) {
          if (new Date(configuration.from) > new Date(configuration.to)) {
            return 'Start date must be before end date'
          }
        }
        const dateValidation = validateDateRangeWithin13Months(configuration.from, configuration.to)
        if (dateValidation !== true) {
          return dateValidation
        }
      }

      return true
    },
  },
  csv: async (api, parameters, title) => {
    await downloadCsv(api, '/insights/alerts/alert-acknowledgement/summary', parameters, title)
  },

  load: async (_api: ApiService, _parameters: AlertAcknowledgementSummaryParams) => {
    const path = `/insights/alerts/alert-acknowledgement/summary?${buildQuery(_parameters)}`
    const response = await _api.get<ApiRow[]>({ path, origin: 'AlertAcknowledgementSummary' })
    if (response.error) return { code: InsightStatusCode.ERROR, message: response.message }
    if (!response.data?.length) return { code: InsightStatusCode.NO_DATA }
    return response.data
  },

  Display: ({ status, data, configuration }) => {
    const showSkeleton = useSkeletonOnConfigChange({ status, data, configuration })

    if (status.code === InsightStatusCode.NOT_CONFIGURED) {
      return <Alert type="info" message={status.message || 'Insight has not been configured'} />
    }
    if (showSkeleton) {
      return <TableSkeleton columns={4} withHeader rowHeight={35} />
    }
    if (status.code !== InsightStatusCode.DISPLAYING && status.code !== InsightStatusCode.LOADING) {
      return null
    }
    if (isInsightStatus(data)) {
      return null
    }
    if (!data?.length) {
      return null
    }

    const parameters = configuration.parameters as AlertAcknowledgementSummaryParams

    const body = data.map((row, index) => {
      const drillBase: InsightConfigurationType = {
        ...configuration,
        slug: 'alert/acknowledgement-detail',
        parameters: {
          companyId: parameters.companyId,
          alertConfigIds: row.alertConfigId,
          severity: row.severity,
          date: parameters.date,
          from: parameters.from,
          to: parameters.to,
          isDrilldown: true,
        },
      }

      const acknowledgedCell: DrilldownCell = {
        value: String(row.acknowledged),
        configuration: {
          ...drillBase,
          parameters: {
            ...drillBase.parameters,
            acknowledged: 'true',
          },
          title: `Alert Acknowledgement Detail: ${row.alertName} - Acknowledged`,
        },
        id: `ack-${row.alertConfigId}-${index}`,
      }

      const notAcknowledgedCell: DrilldownCell = {
        value: String(row.notAcknowledged),
        configuration: {
          ...drillBase,
          parameters: {
            ...drillBase.parameters,
            acknowledged: 'false',
          },
          title: `Alert Acknowledgement Detail: ${row.alertName} - Not Acknowledged`,
        },
        id: `not-ack-${row.alertConfigId}-${index}`,
      }

      return {
        'Alert Name': row.alertName,
        Severity: {
          value: row.severity,
          display: <SeverityPill label={getSeverityLabel(String(row.severity))} severityCode={row.severity} />,
        },
        Acknowledged: row.acknowledged > 0 ? acknowledgedCell : row.acknowledged,
        'Not Acknowledged': row.notAcknowledged > 0 ? notAcknowledgedCell : row.notAcknowledged,
      }
    })

    const headers = Object.keys(body[0])

    return (
      <section className="text-base-content text-base h-full flex flex-col">
        <div className="min-h-0 flex-1">
          <SimpleTable headers={headers} body={body} />
        </div>
      </section>
    )
  },

  Caption: ({ configuration, status }) => {
    const { companies } = useCompany()
    const params = configuration.parameters as AlertAcknowledgementSummaryParams

    if (status.code === InsightStatusCode.ERROR) {
      return ''
    }

    const fields: Record<string, string> = {}

    if (params.companyId) {
      const company = companies.find((c) => c.id === params.companyId)
      if (company) fields['Company'] = company.name
    }

    if (params.severity?.length) {
      const severities = typeof params.severity === 'string' ? [params.severity] : params.severity
      const labels = severities
        .map((s) => getSeverityLabel(s))
        .filter(Boolean)
        .join(', ')
      fields['Severity'] = labels
    }

    addDateToCaptionFields({ ...params, fields, finite: true })

    if (Object.keys(fields).length) {
      return <Caption fields={fields} />
    }
    return ''
  },

  Footer: ({ data, status }) => {
    if (status.code === InsightStatusCode.LOADING && data === undefined) {
      return ''
    }
    if (status.code === InsightStatusCode.NO_DATA) {
      return <div>No records found</div>
    }
    const rows = data?.length
    if (!rows) {
      return <div>No records found</div>
    }
    return (
      <div>
        {rows} row{rows === 1 ? '' : 's'} found
      </div>
    )
  },
}

export default AlertAcknowledgementSummary
